import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  ProgressBar,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import { Eye, Plus, Search, Trash2, Pencil, RotateCcw } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { supabase } from '../lib/supabaseClient';
import {
  fetchProfileLookups,
  findProfileProdi,
  formatProdi,
  hydrateProfile,
} from '../lib/profileLookups';
import {
  canAccessCriteria,
  formatRoleLabel,
  shouldFilterByProfileProdi,
  isSuperadmin as checkSuperadmin,
  isProdiAdmin as checkProdiAdmin,
} from '../lib/accessRules';
import {
  fetchKriteriaByCode,
  getBabNumber,
  KriteriaType,
  parseFieldOptions,
} from '../lib/kriteriaMetadata';

function DynamicKriteriaFormPage() {
  const { kode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [profileProdi, setProfileProdi] = useState(null);
  const [prodiList, setProdiList] = useState([]);
  const [criteria, setCriteria] = useState(null);
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState({ prodi_id: '', data_json: {} });
  const [editId, setEditId] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const isSuperadmin = checkSuperadmin(profile);
  const isProdiAdmin = checkProdiAdmin(profile);
  const canManageRows = isSuperadmin || isProdiAdmin;
  const isNarasi = criteria?.tipe_kriteria === KriteriaType.NARASI;
  const usesSingleFieldFlow = isNarasi && !canManageRows;
  const chapterNumber = getBabNumber(criteria?.kode_kriteria);
  const [creatingNewRecord, setCreatingNewRecord] = useState(false);

  const activeFieldName = useMemo(() => {
    if (!usesSingleFieldFlow) return null;
    const params = new URLSearchParams(location.search);
    const requestedField = params.get('field');
    return criteria?.fields?.some((field) => field.nama_field === requestedField)
      ? requestedField
      : criteria?.fields?.[0]?.nama_field || null;
  }, [criteria, usesSingleFieldFlow, location.search]);

  const visibleFields = useMemo(() => {
    if (!activeFieldName) return criteria?.fields || [];
    return (criteria?.fields || []).filter((field) => field.nama_field === activeFieldName);
  }, [activeFieldName, criteria]);

  const activeRecords = useMemo(
    () => records.filter((item) => !item.is_deleted),
    [records]
  );
  const currentRecord = useMemo(
    () => records.find((item) => item.id === editId) || activeRecords[0] || null,
    [activeRecords, records, editId]
  );
  const canSubmitCurrentForm =
    canManageRows || Boolean(editId) || (usesSingleFieldFlow && Boolean(currentRecord));

  const completion = useMemo(() => {
    const fields = criteria?.fields || [];
    const source = formData.data_json || currentRecord?.data_json || {};
    const filled = fields.filter((field) => {
      const value = source[field.nama_field];
      return value !== null && value !== undefined && String(value).trim() !== '';
    }).length;

    return {
      filled,
      total: fields.length,
      percent: fields.length > 0 ? Math.round((filled / fields.length) * 100) : 0,
    };
  }, [criteria, currentRecord, formData.data_json]);

  useEffect(() => {
    initPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kode, location.search]);

  const initPage = async () => {
    setLoading(true);
    setErrorMsg('');

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      navigate('/');
      return;
    }

    const { data: profileData, error: profileError } = await supabase
      .schema('akreditasi')
      .from('profiles')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (profileError || !profileData) {
      setErrorMsg('Profile user tidak ditemukan.');
      setLoading(false);
      return;
    }

    if (!profileData.is_active) {
      setErrorMsg('Akun nonaktif.');
      setLoading(false);
      return;
    }

    let lookups;
    try {
      lookups = await fetchProfileLookups(supabase);
    } catch (lookupError) {
      setErrorMsg(lookupError.message);
      setLoading(false);
      return;
    }

    const hydratedProfile = hydrateProfile(profileData, lookups);
    let matchedProdi = null;
    const requestedProdiId = new URLSearchParams(location.search).get('prodi');

    if (shouldFilterByProfileProdi(profileData)) {
      matchedProdi = findProfileProdi(profileData, lookups.prodiList);
      if (!matchedProdi) {
        setErrorMsg('Kode prodi pada profile belum cocok dengan tabel prodi.');
        setLoading(false);
        return;
      }
      setProfileProdi(matchedProdi);
      setFormData({ prodi_id: matchedProdi.id, data_json: {} });
    } else if (checkSuperadmin(profileData)) {
      matchedProdi = lookups.prodiList.find((item) => item.id === requestedProdiId) || null;
      if (!matchedProdi) {
        setProfile(hydratedProfile);
        setProdiList(lookups.prodiList);
        setErrorMsg('Pilih program studi dari sidebar untuk membuka struktur kriteria yang tepat.');
        setLoading(false);
        return;
      }
      setProfileProdi(matchedProdi);
      setFormData({ prodi_id: matchedProdi.id, data_json: {} });
    }

    const criteriaData = await fetchKriteriaByCode(supabase, kode, {
      prodiId: matchedProdi?.id,
    });
    if (!criteriaData) {
      setErrorMsg('Kriteria tidak ditemukan, sedang nonaktif, atau tidak digunakan oleh prodi ini.');
      setLoading(false);
      return;
    }

    if (!canAccessCriteria(profileData, criteriaData.kode_kriteria)) {
      setProfile(profileData);
      setErrorMsg(
        `Akun role ${formatRoleLabel(profileData)} tidak dapat membuka ${criteriaData.kode_kriteria}.`
      );
      setLoading(false);
      return;
    }

    setProfile(hydratedProfile);
    setCriteria(criteriaData);
    setProdiList(lookups.prodiList);

    await fetchRecords(criteriaData, matchedProdi?.id || null, profileData);
    setLoading(false);
  };

  const fetchRecords = async (criteriaData = criteria, prodiId = profileProdi?.id, profileData = profile) => {
    if (!criteriaData) return;

    let query = supabase
      .schema('akreditasi')
      .from('kriteria_data')
      .select(`
        *,
        prodi:prodi_id (
          id,
          nama_prodi,
          jenjang
        )
      `)
      .eq('kriteria_id', criteriaData.id);

    const includeArchived = checkSuperadmin(profileData) || checkProdiAdmin(profileData);
    if (!includeArchived) {
      query = query.eq('is_deleted', false);
    }

    if (prodiId) {
      query = query.eq('prodi_id', prodiId);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      setErrorMsg(`Gagal mengambil data: ${error.message}`);
      return;
    }

    const nextRecords = data || [];
    setRecords(nextRecords);

    const nextActiveRecords = nextRecords.filter((item) => !item.is_deleted);

    if (criteriaData.tipe_kriteria === KriteriaType.NARASI && nextActiveRecords.length > 0) {
      const recordForProdi = prodiId
        ? nextActiveRecords.find((item) => item.prodi_id === prodiId)
        : nextActiveRecords[0];
      if (recordForProdi) {
        handleEdit(recordForProdi, { scroll: false });
      }
    }

    return nextRecords;
  };

  const resetForm = () => {
    setFormData((prev) => ({ prodi_id: profileProdi?.id || prev.prodi_id || '', data_json: {} }));
    setEditId(null);
    setCreatingNewRecord(false);
    setMsg('');
    setErrorMsg('');
  };

  const startCreateRecord = () => {
    setFormData((prev) => ({
      prodi_id: profileProdi?.id || prev.prodi_id || '',
      data_json: {},
    }));
    setEditId(null);
    setCreatingNewRecord(true);
    setMsg('');
    setErrorMsg('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleProdiChange = async (prodiId) => {
    setFormData({ prodi_id: prodiId, data_json: {} });
    setEditId(null);
    setCreatingNewRecord(false);
    setErrorMsg('');
    setMsg('');

    if (!prodiId) {
      const criteriaData = await fetchKriteriaByCode(supabase, kode);
      setCriteria(criteriaData);
      await fetchRecords(criteriaData, null, profile);
      return;
    }

    try {
      const criteriaData = await fetchKriteriaByCode(supabase, kode, { prodiId });
      if (!criteriaData) {
        setErrorMsg('Kriteria ini belum digunakan oleh program studi yang dipilih.');
        return;
      }
      setCriteria(criteriaData);
      await fetchRecords(criteriaData, prodiId, profile);
    } catch (error) {
      setErrorMsg(error.message);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      data_json: {
        ...prev.data_json,
        [field.nama_field]: value,
      },
    }));
  };

  const handleEdit = (item, options = {}) => {
    setFormData({
      prodi_id: item.prodi_id || profileProdi?.id || '',
      data_json: item.data_json || {},
    });
    setEditId(item.id);
    setCreatingNewRecord(false);
    if (options.scroll !== false) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    setErrorMsg('');

    if (!criteria || !profile) {
      setErrorMsg('Kriteria atau profile belum tersedia.');
      setSaving(false);
      return;
    }

    const prodiId = profileProdi?.id || formData.prodi_id;
    if (!prodiId) {
      setErrorMsg('Program studi wajib dipilih.');
      setSaving(false);
      return;
    }

    const existingNarasiRecord = isNarasi && !creatingNewRecord
      ? activeRecords.find((item) => item.prodi_id === prodiId)
      : null;
    const targetEditId = editId || existingNarasiRecord?.id || null;

    if (!canManageRows && !targetEditId) {
      setErrorMsg('Baris data harus dibuat oleh Superadmin atau Admin Prodi terlebih dahulu.');
      setSaving(false);
      return;
    }

    const payload = {
      kriteria_id: criteria.id,
      prodi_id: prodiId,
      data_json: formData.data_json,
      updated_by: profile.id,
    };

    if (targetEditId) {
      const { error } = await supabase
        .schema('akreditasi')
        .from('kriteria_data')
        .update(payload)
        .eq('id', targetEditId);

      if (error) {
        setErrorMsg(error.message);
        setSaving(false);
        return;
      }

      setMsg('Data berhasil diperbarui.');
    } else {
      if (isNarasi && canManageRows) {
        const { error: archiveError } = await supabase
          .schema('akreditasi')
          .from('kriteria_data')
          .update({
            is_deleted: true,
            deleted_at: new Date().toISOString(),
            deleted_by: profile.id,
          })
          .eq('kriteria_id', criteria.id)
          .eq('prodi_id', prodiId)
          .eq('is_deleted', false);

        if (archiveError) {
          setErrorMsg(archiveError.message);
          setSaving(false);
          return;
        }
      }

      const { error } = await supabase
        .schema('akreditasi')
        .from('kriteria_data')
        .insert([{ ...payload, created_by: profile.id }]);

      if (error) {
        setErrorMsg(error.message);
        setSaving(false);
        return;
      }

      setMsg('Data berhasil disimpan.');
    }

    await fetchRecords(criteria, profileProdi?.id || formData.prodi_id || null, profile);
    if (!isNarasi) resetForm();
    setCreatingNewRecord(false);
    setSaving(false);
  };

  const handleSoftDelete = async (id) => {
    const { error } = await supabase
      .schema('akreditasi')
      .from('kriteria_data')
      .update({
        is_deleted: true,
        deleted_at: new Date().toISOString(),
        deleted_by: profile.id,
      })
      .eq('id', id);

    if (error) {
      setErrorMsg(error.message);
      return;
    }

    setMsg('Data berhasil dihapus.');
    await fetchRecords(criteria, profileProdi?.id || formData.prodi_id || null, profile);
  };

  const handleRestore = async (item) => {
    if (!canManageRows || !item) return;
    setMsg('');
    setErrorMsg('');

    if (isNarasi) {
      const { error: archiveError } = await supabase
        .schema('akreditasi')
        .from('kriteria_data')
        .update({
          is_deleted: true,
          deleted_at: new Date().toISOString(),
          deleted_by: profile.id,
        })
        .eq('kriteria_id', criteria.id)
        .eq('prodi_id', item.prodi_id)
        .eq('is_deleted', false)
        .neq('id', item.id);

      if (archiveError) {
        setErrorMsg(archiveError.message);
        return;
      }
    }

    const { error } = await supabase
      .schema('akreditasi')
      .from('kriteria_data')
      .update({
        is_deleted: false,
        deleted_at: null,
        deleted_by: null,
        updated_by: profile.id,
      })
      .eq('id', item.id);

    if (error) {
      setErrorMsg(error.message);
      return;
    }

    setMsg('Data berhasil diaktifkan.');
    await fetchRecords(criteria, profileProdi?.id || formData.prodi_id || null, profile);
    handleEdit({ ...item, is_deleted: false }, { scroll: false });
  };

  const renderField = (field) => {
    const value = formData.data_json?.[field.nama_field] || '';

    if (field.tipe_field === 'number') {
      return (
        <Form.Control
          type="number"
          value={value}
          onChange={(e) => handleChange(field, e.target.value)}
          required={field.required}
        />
      );
    }

    if (field.tipe_field === 'select') {
      return (
        <Form.Select
          value={value}
          onChange={(e) => handleChange(field, e.target.value)}
          required={field.required}
        >
          <option value="">Pilih {field.label}</option>
          {parseFieldOptions(field.options).map((option) => (
            <option value={option.value} key={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      );
    }

    return (
      <Form.Control
        as="textarea"
        value={value}
        onChange={(e) => handleChange(field, e.target.value)}
        required={field.required}
      />
    );
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('id-ID', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const filteredRecords = useMemo(() => {
    if (!searchTerm.trim()) return records;
    const keyword = searchTerm.toLowerCase();
    return records.filter((record) =>
      [
        record.prodi ? `${record.prodi.jenjang} ${record.prodi.nama_prodi}` : '',
        ...Object.values(record.data_json || {}),
      ]
        .join(' ')
        .toLowerCase()
        .includes(keyword)
    );
  }, [records, searchTerm]);

  if (loading) {
    return (
      <DashboardLayout title="Kriteria" profile={profile} activeMenu={String(kode || '').toLowerCase()}>
        <div className="page-loading-v2">
          <Spinner animation="border" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={criteria ? `${criteria.kode_kriteria} - ${criteria.nama_kriteria}` : 'Kriteria'}
      breadcrumbs={[
        'Dashboard',
        'Data Akreditasi',
        profileProdi ? formatProdi(profileProdi) : 'Program Studi',
        criteria?.kode_kriteria || 'Kriteria',
      ]}
      profile={profile}
      activeMenu={isSuperadmin && profileProdi ? `${profileProdi.id}:${criteria?.kode_kriteria?.toLowerCase()}` : criteria?.kode_kriteria?.toLowerCase()}
    >
      <Container fluid className="p-0">
        {msg && <Alert variant="success">{msg}</Alert>}
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

        {criteria && (
          <>
            <Card className="criterion-info-card mb-4">
              <Card.Body>
                <Row className="align-items-center g-3">
                  <Col lg={4}>
                    <div className="criterion-team-badge-wrap">
                      <div className="criterion-team-circle">{criteria.kode_kriteria}</div>
                      <div>
                        <div className="criterion-team-title">{criteria.nama_kriteria}</div>
                        <div className="criterion-team-subtitle">
                          BAB {chapterNumber} - {criteria.tipe_kriteria === KriteriaType.NARASI ? 'Narasi' : 'Tabel'}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col lg={4}>
                    <div className="criterion-mini-info">
                      <div className="criterion-badge-row">
                        <Badge bg="primary">{formatRoleLabel(profile)}</Badge>
                      </div>
                      <div className="criterion-mini-text">
                        Kelengkapan data: {completion.filled}/{completion.total} field
                      </div>
                      <ProgressBar now={completion.percent} className="criterion-mini-progress" />
                    </div>
                  </Col>
                  <Col lg={4}>
                    <div className="criterion-program-info">
                      <div className="criterion-label">Program Studi</div>
                      <div className="criterion-program-name">
                        {profileProdi ? formatProdi(profileProdi) : profile?.prodi_display || profile?.prodi || '-'}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="criterion-form-card mb-4">
              <Card.Body>
                <div className="criterion-section-title">
                  {activeFieldName
                    ? criteria.fields.find((field) => field.nama_field === activeFieldName)?.label
                    : creatingNewRecord
                      ? `Tambah Data ${criteria.kode_kriteria}`
                      : editId
                      ? `Edit Data ${criteria.kode_kriteria}`
                      : `Input Data ${criteria.kode_kriteria}`}
                </div>

                <Form onSubmit={handleSubmit}>
                  <Row className="g-3">
                    {shouldFilterByProfileProdi(profile) || isSuperadmin ? (
                      <Col md={6}>
                        <div className="criterion-readonly-field">
                          <span>Program Studi</span>
                          <strong>{profileProdi ? formatProdi(profileProdi) : '-'}</strong>
                        </div>
                      </Col>
                    ) : (
                      <Col md={6}>
                        <Form.Label>Program Studi *</Form.Label>
                        <Form.Select
                          value={formData.prodi_id}
                          onChange={(e) => handleProdiChange(e.target.value)}
                          required
                        >
                          <option value="">Pilih Prodi</option>
                          {prodiList.map((item) => (
                            <option key={item.id} value={item.id}>
                              {formatProdi(item)}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                    )}

                    {isNarasi && records.length > 0 && !canManageRows && (
                      <Col md={6}>
                        <Form.Label>ID Data {criteria.kode_kriteria}</Form.Label>
                        <Form.Select
                          value={editId || ''}
                          onChange={(e) => {
                            const selected = records.find((item) => item.id === e.target.value);
                            if (selected) handleEdit(selected, { scroll: false });
                          }}
                        >
                          {records.map((item) => (
                            <option value={item.id} key={item.id}>
                              {item.id}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                    )}

                    {visibleFields.map((field) => (
                      <Col md={isNarasi ? 12 : 6} key={field.id}>
                        <Form.Label>
                          {field.label}
                          {field.required ? ' *' : ''}
                        </Form.Label>
                        {renderField(field)}
                      </Col>
                    ))}
                  </Row>

                  <div className="criterion-form-actions">
                    {canManageRows && (
                      <Button variant="outline-secondary" type="button" onClick={resetForm}>
                        Reset
                      </Button>
                    )}
                    <Button className="save-btn-v2" type="submit" disabled={saving || !canSubmitCurrentForm}>
                      {saving ? 'Menyimpan...' : 'Simpan Data'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            {isNarasi && !canManageRows ? (
              <Card className="criterion-table-card">
                <Card.Body>
                  <div className="section-head-v2 mb-3">
                    <div>
                      <h4 className="section-title-v2">Data {criteria.kode_kriteria} Saat Ini</h4>
                    </div>
                    <Badge bg={currentRecord ? 'success' : 'secondary'}>
                      {currentRecord ? 'Aktif' : 'Belum Ada Data'}
                    </Badge>
                  </div>
                  {currentRecord ? (
                    <div className="criterion-current-record">
                      <div className="criterion-current-meta">
                        <div>
                          <span>ID Data</span>
                          <strong>{currentRecord.id}</strong>
                        </div>
                        <div>
                          <span>Diperbarui</span>
                          <strong>{formatDateTime(currentRecord.updated_at)}</strong>
                        </div>
                      </div>
                      <div className="criterion-current-fields">
                        {criteria.fields.map((field) => (
                          <section
                            className={`criterion-current-field ${field.nama_field === activeFieldName ? 'active' : ''}`}
                            key={field.id}
                          >
                            <h5>{field.label}</h5>
                            <p>{String(currentRecord.data_json?.[field.nama_field] || '-')}</p>
                          </section>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="criterion-softdelete-note">
                      Belum ada data aktif untuk program studi ini.
                    </div>
                  )}
                </Card.Body>
              </Card>
            ) : (
              <Card className="criterion-table-card">
                <Card.Body>
                  <div className="criterion-toolbar">
                    <div className="criterion-toolbar-left">
                      {canManageRows && (
                        <Button className="toolbar-btn-primary" onClick={startCreateRecord}>
                          <Plus size={16} className="me-2" />
                          Tambah Data
                        </Button>
                      )}
                    </div>
                    <div className="criterion-toolbar-right">
                      <InputGroup className="criterion-search-box">
                        <InputGroup.Text>
                          <Search size={16} />
                        </InputGroup.Text>
                        <Form.Control
                          placeholder="Cari data..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                    </div>
                  </div>

                  <div className="criterion-table-wrap">
                    <Table responsive hover className="criterion-table align-middle">
                      <thead>
                        <tr>
                          <th>No</th>
                          <th>Prodi</th>
                          {criteria.fields.slice(0, 5).map((field) => (
                            <th key={field.id}>{field.label}</th>
                          ))}
                          <th>Status</th>
                          <th>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRecords.length > 0 ? (
                          filteredRecords.map((item, index) => (
                            <tr key={item.id}>
                              <td>{index + 1}</td>
                              <td>{item.prodi ? `${item.prodi.jenjang} - ${item.prodi.nama_prodi}` : '-'}</td>
                              {criteria.fields.slice(0, 5).map((field) => (
                                <td key={field.id}>
                                  {String(item.data_json?.[field.nama_field] || '-').slice(0, 55)}
                                </td>
                              ))}
                              <td>
                                <Badge bg={item.is_deleted ? 'secondary' : 'success'}>
                                  {item.is_deleted ? 'Arsip' : 'Aktif'}
                                </Badge>
                              </td>
                              <td>
                                <div className="table-action-group">
                                  <Button size="sm" variant="outline-primary" onClick={() => handleEdit(item)}>
                                    <Pencil size={14} />
                                  </Button>
                                  {canManageRows && (
                                    item.is_deleted ? (
                                      <Button size="sm" variant="outline-success" onClick={() => handleRestore(item)}>
                                        <RotateCcw size={14} />
                                      </Button>
                                    ) : (
                                      <Button size="sm" variant="outline-danger" onClick={() => handleSoftDelete(item.id)}>
                                        <Trash2 size={14} />
                                      </Button>
                                    )
                                  )}
                                  <Button size="sm" variant="outline-secondary" onClick={() => setSelectedDetail(item)}>
                                    <Eye size={14} />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={criteria.fields.slice(0, 5).length + 4} className="text-center py-4">
                              Belum ada data.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}
          </>
        )}

        <Modal show={!!selectedDetail} onHide={() => setSelectedDetail(null)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Detail Data {criteria?.kode_kriteria}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedDetail && (
              <Table responsive bordered className="criterion-table mb-0">
                <tbody>
                  {criteria.fields.map((field) => (
                    <tr key={field.id}>
                      <th style={{ width: '34%' }}>{field.label}</th>
                      <td>{String(selectedDetail.data_json?.[field.nama_field] || '-')}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setSelectedDetail(null)}>
              Tutup
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </DashboardLayout>
  );
}

export default DynamicKriteriaFormPage;
